import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { X, Settings, Cookie } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';

const COOKIE_CONSENT_KEY = 'anw_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'anw_cookie_preferences';
const COOKIE_CONSENT_DATE_KEY = 'anw_cookie_consent_date';

export interface CookiePreferences {
  necessary: boolean; // Always true, cannot be disabled
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  functional: true,
  analytics: true,
  marketing: true,
};

export function getCookiePreferences(): CookiePreferences | null {
  const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
}

export function hasCookieConsent(): boolean {
  return localStorage.getItem(COOKIE_CONSENT_KEY) !== null;
}

// Function to open cookie settings from anywhere (e.g., footer)
export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent('open-cookie-settings'));
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setVisible(true);
    } else {
      const savedPrefs = getCookiePreferences();
      if (savedPrefs) {
        setPreferences(savedPrefs);
      }
    }

    // Listen for external requests to open settings
    const handleOpenSettings = () => {
      setShowSettings(true);
    };
    window.addEventListener('open-cookie-settings', handleOpenSettings);
    return () => window.removeEventListener('open-cookie-settings', handleOpenSettings);
  }, []);

  const logConsent = async (prefs: CookiePreferences, consentType: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('gdpr_consent_log').insert({
          user_id: user.id,
          consent_type: 'cookies',
          consented: true,
          user_agent: navigator.userAgent,
        });
      }
    } catch {
      // Silent fail - consent logging is non-critical
    }
  };

  const savePreferences = async (prefs: CookiePreferences, type: 'accept_all' | 'reject_all' | 'custom') => {
    localStorage.setItem(COOKIE_CONSENT_KEY, type);
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem(COOKIE_CONSENT_DATE_KEY, new Date().toISOString());
    setPreferences(prefs);
    setVisible(false);
    setShowSettings(false);
    
    await logConsent(prefs, type);
  };

  const handleAcceptAll = () => {
    savePreferences(DEFAULT_PREFERENCES, 'accept_all');
  };

  const handleRejectAll = () => {
    savePreferences({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    }, 'reject_all');
  };

  const handleSavePreferences = () => {
    savePreferences(preferences, 'custom');
  };

  const updatePreference = (key: keyof CookiePreferences, value: boolean) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  if (!visible && !showSettings) return null;

  

  return (
    <>
      {visible && (
        <div 
          role="dialog" 
          aria-label="Cookie consent" 
          className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-card border-t border-border shadow-lg animate-in slide-in-from-bottom-4 duration-300"
        >
          <div className="container max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1 pr-4">
              <p className="text-sm text-foreground">
                We use cookies to enhance your browsing experience, serve personalized ads, and analyze traffic. 
                You can customize your preferences or accept all cookies. Read our{' '}
                <Link to="/privacy" className="text-primary hover:underline font-medium">
                  Privacy Policy
                </Link>{' '}
                for more information.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)}>
                <Settings className="h-4 w-4 mr-1" />
                Preferences
              </Button>
              <Button variant="outline" size="sm" onClick={handleRejectAll}>
                Reject All
              </Button>
              <Button size="sm" onClick={handleAcceptAll}>
                Accept All
              </Button>
            </div>
            <button
              onClick={handleRejectAll}
              className="absolute top-2 right-2 sm:hidden p-1 text-muted-foreground hover:text-foreground"
              aria-label="Close cookie banner"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cookie Preferences</DialogTitle>
            <DialogDescription>
              Choose which cookies you want to allow. You can change these settings at any time.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Necessary Cookies */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">Necessary</Label>
                <p className="text-sm text-muted-foreground">
                  Essential for the website to function. Cannot be disabled.
                </p>
              </div>
              <Switch checked disabled aria-label="Necessary cookies (always enabled)" />
            </div>

            {/* Functional Cookies */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">Functional</Label>
                <p className="text-sm text-muted-foreground">
                  Enable personalized features and remember your preferences.
                </p>
              </div>
              <Switch 
                checked={preferences.functional} 
                onCheckedChange={(checked) => updatePreference('functional', checked)}
                aria-label="Functional cookies"
              />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">Analytics</Label>
                <p className="text-sm text-muted-foreground">
                  Help us understand how visitors interact with the website.
                </p>
              </div>
              <Switch 
                checked={preferences.analytics} 
                onCheckedChange={(checked) => updatePreference('analytics', checked)}
                aria-label="Analytics cookies"
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">Marketing</Label>
                <p className="text-sm text-muted-foreground">
                  Used to deliver personalized advertisements.
                </p>
              </div>
              <Switch 
                checked={preferences.marketing} 
                onCheckedChange={(checked) => updatePreference('marketing', checked)}
                aria-label="Marketing cookies"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleRejectAll} className="w-full sm:w-auto">
              Reject All
            </Button>
            <Button onClick={handleSavePreferences} className="w-full sm:w-auto">
              Save Preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
